import React from "react";
import { AlertTriangle } from "lucide-react";
import {
  isNftCostBasisUnknown,
  isNftSalePaymentRow,
} from "../utils/nftCostBasis";

function isJuicySupplyHistoryIssue(tx = {}) {
  return Boolean(
    tx.juicyfinanceUnmatchedWithdrawal ||
      tx.reviewReason === "MISSING_JUICY_SUPPLY_HISTORY" ||
      tx.taxResolutionStatus === "needs_supply_history_review" ||
      String(tx.classification || tx.type || "").toLowerCase() ===
        "juicyfinance_unmatched_withdrawal_principal"
  );
}

function isCostBasisIssue(tx = {}) {
  if (isJuicySupplyHistoryIssue(tx)) return false;

  return Boolean(
    tx.missingCostBasis ||
      tx.costBasisUnknown ||
      tx.pricingStatus === "missing_cost_basis" ||
      tx.displayPriceStatus === "COST_BASIS_NOT_AVAILABLE" ||
      tx.displayPriceStatus === "COST_BASIS_UNKNOWN" ||
      tx.taxResolutionStatus === "needs_cost_basis_review" ||
      tx.requiresUserCostBasis ||
      tx.userCostBasisRequired ||
      isNftCostBasisUnknown(tx)
  );
}

function isTokenPriceIssue(tx = {}) {
  return Boolean(
    tx.pricingMissing ||
      tx.priceMissing ||
      tx.missingFiatPrice ||
      tx.pricingStatus === "missing_price" ||
      tx.displayPriceStatus === "PRICE_NOT_AVAILABLE"
  );
}

export function getPreviewTaxIssueKind(tx) {
  if (isJuicySupplyHistoryIssue(tx)) {
    return "juicy_supply_history";
  }

  const costBasis = isCostBasisIssue(tx);
  const priceMissing = isTokenPriceIssue(tx);

  if (costBasis) {
    return isNftSalePaymentRow(tx) ? "nft_cost_basis" : "manual";
  }

  if (priceMissing || Boolean(tx?.taxComputationSkipped)) {
    return "price";
  }

  return null;
}

function getIssueCopy(language = "de", issueKind = "price") {
  const isEn = String(language || "").toLowerCase().startsWith("en");

  if (isEn) {
    switch (issueKind) {
      case "nft_cost_basis":
        return {
          title: "No cost basis found",
          subtitle:
            "NFT purchase could not be determined from transaction history. Profit/loss is excluded until acquisition price and date are added.",
        };
      case "manual":
        return {
          title: "Acquisition cost unknown",
          subtitle: "This transaction requires manual review.",
        };
      case "juicy_supply_history":
        return {
          title: "JuicyFinance deposit history missing",
          subtitle:
            "Withdrawal not booked as lending income. Prior supply/deposit principal was not found in FIFO history.",
        };
      default:
        return {
          title: "Token price unavailable",
          subtitle: "This transaction is currently excluded from tax calculation.",
        };
    }
  }

  switch (issueKind) {
    case "nft_cost_basis":
      return {
        title: "Kein Einstand gefunden",
        subtitle:
          "NFT-Kauf konnte aus der Transaktionshistorie nicht ermittelt werden. Gewinn/Verlust wird erst nach manueller Ergänzung von Einstand und Kaufdatum einbezogen.",
      };
    case "manual":
      return {
        title: "Anschaffungskosten unbekannt",
        subtitle: "Diese Transaktion benötigt manuelle Prüfung.",
      };
    case "juicy_supply_history":
      return {
        title: "JuicyFinance-Deposit-Historie fehlt",
        subtitle:
          "Withdrawal nicht als Lending Income gebucht. Vorherige Supply-/Deposit-Principal-Lots fehlen in der FIFO-Historie.",
      };
    default:
      return {
        title: "Token-Preis nicht verfügbar",
        subtitle: "Diese Transaktion ist aktuell von der Steuerberechnung ausgeschlossen.",
      };
  }
}

export function PreviewTaxIssueBadge({
  language,
  issueKind,
  align = "center",
  compact = true,
}) {
  if (!issueKind) return null;

  const { title, subtitle } = getIssueCopy(language, issueKind);

  const alignClass =
    align === "start"
      ? "items-start text-left"
      : align === "end"
        ? "items-end text-right"
        : "items-center text-center";

  return (
    <div
      className={[
        "inline-flex w-full max-w-full flex-col rounded-lg border border-amber-200/90 bg-amber-50/90 shadow-sm",
        compact ? "gap-0 px-2 py-1" : "gap-0.5 px-2.5 py-1.5",
        alignClass,
      ].join(" ")}
    >
      <div className="inline-flex items-center justify-center gap-1 text-[10px] font-semibold leading-none text-amber-900">
        <span className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
          <AlertTriangle className="h-2.5 w-2.5" strokeWidth={2.4} />
        </span>
        <span className="leading-tight">{title}</span>
      </div>

      <div className="mt-0.5 text-[9px] font-medium leading-[1.2] text-amber-800/88">{subtitle}</div>
    </div>
  );
}

export function PreviewTaxIssueCell({
  language,
  issueKind,
  colSpan = 3,
  className = "",
}) {
  if (!issueKind) return null;

  return (
    <td
      colSpan={colSpan}
      className={["px-4 py-2 align-middle", className].filter(Boolean).join(" ")}
    >
      <PreviewTaxIssueBadge language={language} issueKind={issueKind} align="center" compact />
    </td>
  );
}
